"use client";

import { useMemo, useState } from "react";

import { Oriented } from "@/data/constants/oriented";
import { useLang } from "@/hooks/use-lang";
import { useUpdateQuery } from "@/hooks/use-update-query";

import { Button } from "ui/core/button";
import { Select } from "ui/core/select";

import {
  getEnterOptions,
  getExitOptions,
} from "../data/control-pannel-options";

type Props = {
  oriented: Oriented;
  enter?: "4" | "5";
  exit?: "1" | "2" | "3";
};

export const ControlPanel = ({
  oriented,
  enter: enterOptionParam = "4",
  exit: exitOptionParam = "2",
}: Props) => {
  const { t } = useLang();
  const { updateQueries } = useUpdateQuery();

  const enterOptions = useMemo(() => getEnterOptions({ t }), [t]);
  const exitOptions = useMemo(
    () => getExitOptions({ t, oriented }),
    [t, oriented]
  );

  const enterOption = useMemo(
    () =>
      enterOptions.find((value) => value.includes(enterOptionParam)) ??
      t.analysis.shared.scoreOption.getScoreMore(4),
    [enterOptionParam, enterOptions, t.analysis.shared.scoreOption]
  );
  const exitOption = useMemo(
    () =>
      exitOptions.find((value) => value.includes(exitOptionParam)) ??
      t.analysis.shared.scoreOption.getScoreLess(2),
    [exitOptionParam, exitOptions, t.analysis.shared.scoreOption]
  );

  const [options, setOptions] = useState({ enterOption, exitOption });

  const handleSubmit = () => {
    updateQueries([
      {
        name: "enter",
        value:
          options.enterOption
            .split(" ")
            .find((value) => ["1", "2", "3", "4", "5"].includes(value)) ?? "4",
      },
      {
        name: "exit",
        value:
          options.exitOption
            .split(" ")
            .find((value) => ["1", "2", "3", "4", "5"].includes(value)) ?? "2",
      },
    ]);
  };

  return (
    <div className="flex flex-col gap-5 sm:flex-row">
      <div className="flex w-full gap-6 sm:max-w-sm">
        <div className="flex w-full flex-col gap-2">
          <span className="text-sm font-medium text-white">
            {t.analysis.backtest.enter}
          </span>
          <Select
            options={enterOptions}
            value={options.enterOption}
            onChange={(e) =>
              setOptions((options) => ({
                ...options,
                enterOption: e.target.value,
              }))
            }
          />
        </div>
        <div className="flex w-full flex-col gap-2">
          <span className="text-sm font-medium text-white">
            {t.analysis.backtest.exit}
          </span>
          <Select
            options={exitOptions}
            value={options.exitOption}
            onChange={(e) =>
              setOptions((options) => ({
                ...options,
                exitOption: e.target.value,
              }))
            }
          />
        </div>
      </div>
      <Button
        className="mt-auto h-9 w-full whitespace-nowrap py-0 text-sky-700 sm:w-fit"
        variant="secondary"
        onClick={handleSubmit}
      >
        {t.analysis.backtest.run}
      </Button>
    </div>
  );
};
